import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Conversation } from 'src/app/interfaces/conversation';
import {
  getMessageBodyText,
  Message,
  MessageType,
} from 'src/app/interfaces/message';
import { Sale } from 'src/app/interfaces/sale';
import { ApiService } from 'src/app/services/api.service';
import Swal from 'sweetalert2';
import { conversationSteps, Steps } from '../../constants/constants';
import { Router } from '@angular/router';
import { StoreContextService } from 'src/app/services/store-context.service';
import {
  ConversationCacheService,
  filterAllowedMessages,
  getMessageKey,
  isOptimisticMessage,
  isSameMessage,
} from 'src/app/services/conversation-cache.service';
import { RefreshPhase } from 'src/app/directives/refresh-surface.directive';

@Component({
  selector: 'app-chatroom',
  templateUrl: './chatroom.component.html',
  styleUrls: ['./chatroom.component.scss'],
})
export class ChatroomComponent implements OnInit, OnChanges {
  @Input() selectedConversation = {} as Conversation;
  @Input() socketMessage!: Message;
  @Input() refreshPhase: RefreshPhase = 'idle';
  @Output() conversationResolved = new EventEmitter<Conversation>();
  @ViewChild('mainContainer') mainContainer!: ElementRef;
  messages: Message[] = [];
  page: number = 0;
  quantity: number = 20;
  loadingMessages: boolean = true;
  loadMoreMessages: boolean = true;
  loading = true;
  isLoadingConversationAssistant = false;
  messageText = '';
  isMessageSending = false;
  customerSales = [] as Sale[];
  saleCount!: number;
  lastSaleDate!: string;
  detailAddressUser = '';
  inputIsDisabled = true;

  get conversationStep():
    | {
      name: string;
      value: Steps;
      label: string;
      icon: string;
    }
    | undefined {
    return this.getConversationStep(this.selectedConversation.step);
  }

  get chatInputPlaceholder(): string {
    if (this.isLoadingConversationAssistant) {
      return 'Resolviendo conversación...';
    }

    return this.chatInputIsEnabled()
      ? 'Inicia la conversación aquí'
      : 'Primero desactiva el bot para escribir tu respuesta';
  }

  saleUrl: string | null = null;

  // El componente ya no se recrea al cambiar de conversacion (ver *ngIf en
  // layout.component.html), por eso cada fetch se marca con una secuencia:
  // si el usuario ya cambio de chat cuando la promesa resuelve, se descarta
  // la escritura sobre this.messages (solo se actualiza la cache de aquel telefono)
  private loadSeq = 0;

  constructor(
    private api: ApiService,
    private router: Router,
    private storeContext: StoreContextService,
    private cache: ConversationCacheService
  ) { }

  ngOnInit(): void {
    this.initForConversation();
  }

  ngOnChanges(changes: SimpleChanges) {
    // firstChange ya se maneja en ngOnInit; el resto de los cambios de
    // conversacion (el chatroom ya no se destruye/recrea) se atienden aqui
    if (
      changes['selectedConversation'] &&
      !changes['selectedConversation'].firstChange
    ) {
      this.initForConversation();
    }

    // El componente ahora persiste entre conversaciones: se valida que el
    // mensaje del socket pertenezca a la conversacion actualmente abierta
    if (
      changes['socketMessage'] &&
      !changes['socketMessage'].firstChange &&
      this.socketMessage.type &&
      this.socketMessage.conversationId === this.selectedConversation.id
    ) {
      // El socket de layout ya pudo haber sincronizado este mensaje en la
      // cache (mismo arreglo que this.messages) antes de que Angular
      // dispare este ngOnChanges; se evita empujarlo por duplicado
      const alreadyInMessages = this.messages.some(m =>
        isSameMessage(m, this.socketMessage)
      );

      // El backend reenvia por socket el mensaje que el asesor acaba de
      // mandar; si ya se pinto de forma optimista (sin ids), se reemplaza
      // por la version del socket en vez de duplicarlo
      const echoedBody = getMessageBodyText(this.socketMessage.data?.body);
      const echoedIndex = this.socketMessage.isBot
        ? this.messages.findIndex(
            m =>
              m.isBot &&
              isOptimisticMessage(m) &&
              getMessageBodyText(m.data?.body) === echoedBody
          )
        : -1;

      if (echoedIndex > -1) {
        this.messages[echoedIndex] = this.socketMessage;
      } else if (!alreadyInMessages) {
        this.messages.push(this.socketMessage);
        if (this.socketMessage.type === 'interactive') {
          const firstMessages = this.messages.slice(
            0,
            this.messages.length - 5
          );
          const lastFive = this.messages.slice(-5);
          const sortedLastFive = lastFive.sort((a, b) => a.epoch - b.epoch);
          this.messages = firstMessages.concat(sortedLastFive);
        }
        this.scrollToBottom(true);
      }
      this.persistMessagesToCache();
    }
  }

  // Centraliza la inicializacion que antes vivia solo en ngOnInit; se
  // reutiliza en cada cambio de conversacion porque el componente ya no se
  // destruye/recrea
  private initForConversation(): void {
    this.page = 0;
    this.loadMoreMessages = true;
    this.messageText = '';
    // saleCount/lastSaleDate son `!:` (definite assignment) porque el
    // tooltip los tipa como no-opcionales; se reinician con este cast para
    // no mostrar datos del cliente anterior mientras resuelve el fetch
    this.saleCount = undefined as unknown as number;
    this.lastSaleDate = undefined as unknown as string;
    this.detailAddressUser = '';
    this.isMessageSending = false;
    this.loading = true;
    this.loadingMessages = true;
    this.messages = [];

    this.loadConversationMessages();
    this.getDetailCustomer();
    this.resolveSaleUrl();

    // Agrega el telefono a la URL para poder compartir el link del chat
    this.router.navigate([], {
      queryParams: {
        phone: this.selectedConversation.phone,
      },
      queryParamsHandling: 'merge',
    });
  }

  private persistMessagesToCache(
    phone: string = this.selectedConversation.phone,
    messages: Message[] = this.messages,
    page: number = this.page,
    loadMoreMessages: boolean = this.loadMoreMessages
  ): void {
    this.cache.set(phone, { messages, page, loadMoreMessages });
  }

  // Cuando la conversacion tiene una compra exitosa asociada (sale_id) se
  // muestra un link directo al detalle de la orden en el back office
  private async resolveSaleUrl(): Promise<void> {
    this.saleUrl = null;
    if (!this.selectedConversation.sale_id) return;
    await this.storeContext.getStoreUrl();
    this.saleUrl = this.storeContext.getSaleUrl(
      this.selectedConversation.sale_id
    );
  }

  chatInputIsEnabled() {
    return (
      !this.isLoadingConversationAssistant &&
      (this.selectedConversation.is_assistance_required ||
        !this.selectedConversation.is_bot_enabled)
    );
  }

  async getDetailCustomer(forceRefresh = false): Promise<void> {
    const { customer_id, phone } = this.selectedConversation;

    const cached = this.cache.getCustomerDetail(phone);
    if (cached && !forceRefresh) {
      this.saleCount = cached.saleCount;
      this.lastSaleDate = cached.lastSaleDate;
      this.detailAddressUser = cached.detailAddressUser;
      return;
    }

    const detail: {
      saleCount?: number;
      lastSaleDate?: string;
      detailAddressUser: string;
    } = {
      saleCount: forceRefresh ? this.saleCount : undefined,
      lastSaleDate: forceRefresh ? this.lastSaleDate : undefined,
      detailAddressUser: forceRefresh ? this.detailAddressUser : '',
    };
    const [customerResult, addressResult] = await Promise.allSettled([
      this.api.getDetailCustomer(customer_id),
      this.api.getDetailAddressUser(phone),
    ]);

    if (customerResult.status === 'fulfilled') {
      detail.saleCount = customerResult.value.count;
      detail.lastSaleDate = customerResult.value.lastSale?.createdAt;
    } else {
      console.error('Error getting customer details: ', customerResult.reason);
    }

    if (addressResult.status === 'fulfilled') {
      detail.detailAddressUser = addressResult.value.address;
    } else {
      console.error('Error getting address details: ', addressResult.reason);
    }

    if (phone === this.selectedConversation.phone) {
      if (detail.saleCount !== undefined) this.saleCount = detail.saleCount;
      if (detail.lastSaleDate !== undefined)
        this.lastSaleDate = detail.lastSaleDate;
      this.detailAddressUser = detail.detailAddressUser;
    }

    // Solo se cachea si ambas consultas resolvieron con datos reales; de lo
    // contrario un fallo de red quedaria cacheado como si fuera valido
    if (
      customerResult.status === 'fulfilled' &&
      addressResult.status === 'fulfilled' &&
      detail.saleCount !== undefined &&
      detail.lastSaleDate !== undefined
    ) {
      this.cache.setCustomerDetail(phone, {
        saleCount: detail.saleCount,
        lastSaleDate: detail.lastSaleDate,
        detailAddressUser: detail.detailAddressUser,
      });
    }
  }

  async loadConversationMessages(): Promise<void> {
    const phone = this.selectedConversation.phone;
    const seq = ++this.loadSeq;
    const cached = this.cache.get(phone);

    if (cached) {
      // Stale-while-revalidate: se pinta de inmediato lo que ya esta en
      // cache (sin loader) y se refresca en background
      this.messages = cached.messages;
      this.page = cached.page;
      this.loadMoreMessages = cached.loadMoreMessages;
      this.loading = false;
      this.loadingMessages = false;
      this.scrollToBottom(false);

      try {
        const res = await this.api.getConversationHistoryByPhone(
          phone,
          0,
          this.quantity
        );
        // El usuario cambio de conversacion mientras esto resolvia
        if (seq !== this.loadSeq) return;
        const fresh = filterAllowedMessages(res.messages ?? []);
        const reconciled = this.reconcileMessages(this.messages, fresh);
        this.messages = reconciled;
        // La pagina/loadMoreMessages no se reinician: solo se refresco la
        // "punta" reciente, el historial ya paginado hacia atras se conserva
        this.persistMessagesToCache(
          phone,
          reconciled,
          this.page,
          this.loadMoreMessages
        );
      } catch (error) {
        console.error(error);
      }
      return;
    }

    try {
      const res = await this.api.getConversationHistoryByPhone(
        phone,
        this.page,
        this.quantity
      );
      const filtered = filterAllowedMessages(res.messages ?? []);
      if (seq !== this.loadSeq) {
        // El usuario ya cambio de chat: se siembra la cache de aquel
        // telefono pero no se pisa la vista actual
        this.persistMessagesToCache(phone, filtered, 0, true);
        return;
      }
      this.messages = filtered;
      this.persistMessagesToCache(phone, this.messages, 0, true);
      this.scrollToBottom(false);
      this.loading = false;
      this.loadingMessages = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  async refreshConversationData(): Promise<void> {
    const phone = this.selectedConversation.phone;
    const seq = ++this.loadSeq;
    const currentPage = this.page;
    const canLoadMore = this.loadMoreMessages;
    const scrollContainer = this.mainContainer?.nativeElement as
      | HTMLElement
      | undefined;
    const previousScrollTop = scrollContainer?.scrollTop ?? 0;
    const wasNearBottom = scrollContainer
      ? scrollContainer.scrollHeight -
          scrollContainer.scrollTop -
          scrollContainer.clientHeight <
        80
      : true;

    const [messagesResult] = await Promise.allSettled([
      this.api.getConversationHistoryByPhone(phone, 0, this.quantity),
      this.getDetailCustomer(true),
      this.resolveSaleUrl(),
    ]);

    if (
      messagesResult.status === 'rejected' ||
      seq !== this.loadSeq ||
      phone !== this.selectedConversation.phone
    ) {
      if (messagesResult.status === 'rejected') {
        console.error(messagesResult.reason);
      }
      return;
    }

    const fresh = filterAllowedMessages(messagesResult.value.messages ?? []);
    this.messages = this.reconcileMessages(this.messages, fresh);
    this.page = currentPage;
    this.loadMoreMessages = canLoadMore;
    this.persistMessagesToCache(phone, this.messages, currentPage, canLoadMore);

    setTimeout(() => {
      if (!scrollContainer || phone !== this.selectedConversation.phone) return;
      if (wasNearBottom) {
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight });
      } else {
        scrollContainer.scrollTop = previousScrollTop;
      }
    });
  }

  // Combina el historial cacheado con la pagina 0 fresca del servidor:
  // conserva el historial ya paginado hacia atras (mas viejo que el primer
  // mensaje fresco) y los mensajes mas nuevos u optimistas que el servidor
  // aun no reconoce (id/msgId vacios)
  private reconcileMessages(
    oldMessages: Message[],
    freshMessages: Message[]
  ): Message[] {
    if (freshMessages.length === 0) {
      return oldMessages;
    }

    const freshKeys = new Set(
      freshMessages.map(getMessageKey).filter((key): key is string => !!key)
    );
    const firstFreshEpoch = freshMessages[0].epoch;
    const lastFreshEpoch = freshMessages[freshMessages.length - 1].epoch;

    const olderHistory: Message[] = [];
    const newerOrOptimistic: Message[] = [];
    for (const message of oldMessages) {
      const key = getMessageKey(message);
      if (key && freshKeys.has(key)) continue;
      if (message.epoch < firstFreshEpoch) {
        olderHistory.push(message);
      } else if (
        isOptimisticMessage(message) ||
        message.epoch > lastFreshEpoch
      ) {
        newerOrOptimistic.push(message);
      }
    }

    return [...olderHistory, ...freshMessages, ...newerOrOptimistic];
  }

  async onAiBotStatusChange(enable: boolean): Promise<void> {
    const { phone } = this.selectedConversation;
    if (!enable) {
      const result = await Swal.fire({
        title:
          '<span class="text-2xl text-text font-bold">¿Quieres <span style="color: var(--color-surface-error)">desactivar</span> la inteligencia Artificial para el chat con el siguiente número?</span><br>' +
          '<span class="text-lg text-text-brand bg-surface-brand-subtle px-[16px] py-[8px] rounded-lg" >' +
          this.selectedConversation.phone +
          '</span>',
        text: 'Desactivar la inteligencia artificial impedirá que el bot responda a los mensajes en esta conversación. Esto pondrá en pausa el flujo de la conversación actual y permitirá que envíes mensajes de forma manual',
        icon: 'warning',
        iconColor: 'var(--color-surface-error)',
        showCancelButton: true,
        confirmButtonText: 'Desactivar',
        cancelButtonText: 'Cancelar',
        customClass: {
          cancelButton:
            'order-1 custom-swal-button-width custom-swal-button-cancel',
          confirmButton:
            'order-2 custom-swal-button-width custom-swal-button-confirm',
        },
        width: '610px',
      });

      if (result.isConfirmed) {
        try {
          await this.api.setAiBotStatus(phone, false);
          this.selectedConversation.is_bot_enabled = false;
          this.messageText = '';
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      const result = await Swal.fire({
        title:
          '<span class="text-2xl text-text font-bold">¿Quieres <span style="color: var(--color-surface-success)">activar</span> la inteligencia Artificial para el chat con el siguiente número?</span><br>' +
          '<span class="text-lg text-text-brand bg-surface-brand-subtle px-[16px] py-[8px] rounded-lg" >' +
          this.selectedConversation.phone +
          '</span>',
        text: 'Activar la inteligencia artificial habilitará que el bot responda a los mensajes en esta conversación. Esto resumirá el flujo de la conversación actual y permitirá que el bot responda a los mensajes entrantes',
        icon: 'warning',
        iconColor: 'var(--color-surface-success)',
        showCancelButton: true,
        confirmButtonText: 'Activar',
        cancelButtonText: 'Cancelar',
        customClass: {
          cancelButton:
            'order-1 custom-swal-button-width custom-swal-button-cancel',
          confirmButton:
            'order-2 custom-swal-button-width custom-swal-button-confirm-enable',
        },
        width: '610px',
      });
      if (result.isConfirmed) {
        try {
          await this.api.setAiBotStatus(phone, true);
          this.selectedConversation.is_bot_enabled = true;
        } catch (error) {
          console.error(error);
        }
      }
    }
  }

  scrollToBottom(animation: boolean): void {
    setTimeout(
      () => {
        if (this.mainContainer) {
          const container = this.mainContainer.nativeElement;
          if (animation) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'smooth',
            });
          } else {
            container.scrollTo({ top: container.scrollHeight });
          }
        }
      },
      animation ? 100 : 1
    );
  }
  onButtonClick(): void {
    const text = this.messageText.trim();
    if (text === '' || this.isMessageSending || !this.chatInputIsEnabled()) {
      return;
    }
    this.isMessageSending = true;
    // Push optimista antes del POST: el eco del socket puede llegar antes
    // que la respuesta http, y ngOnChanges dedupe contra este mensaje
    // (id/msgId vacios) para que no aparezca repetido
    const newMessage: Message = {
      id: '',
      msgId: '',
      type: MessageType.TEXT,
      data: {
        body: text,
      },
      isBot: true,
      conversationId: this.selectedConversation.id,
      epoch: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.messages.push(newMessage);
    this.persistMessagesToCache();
    this.messageText = '';
    this.scrollToBottom(true);
    this.api
      .sendMessages(this.selectedConversation.id, text)
      .catch(err => {
        console.error(err);
        const index = this.messages.indexOf(newMessage);
        if (index > -1) {
          this.messages.splice(index, 1);
          this.persistMessagesToCache();
        }
        this.messageText = text;
      })
      .finally(() => {
        this.isMessageSending = false;
      });
  }

  async loadMoreConversationMessages() {
    try {
      if (this.loadingMessages) return;
      this.loadingMessages = true;
      const phone = this.selectedConversation.phone;
      this.page++;
      const res = await this.api.getConversationHistoryByPhone(
        phone,
        this.page,
        this.quantity
      );
      const filtered = filterAllowedMessages(res.messages ?? []);
      if (filtered.length === 0) {
        this.loadMoreMessages = false;
      } else {
        this.messages = filtered.concat(this.messages);
      }
      this.persistMessagesToCache(
        phone,
        this.messages,
        this.page,
        this.loadMoreMessages
      );
      this.loading = false;
      this.loadingMessages = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }
  async markReady() {
    try {
      this.isLoadingConversationAssistant = true;
      const response = await this.api.conversationAssistantReady(
        this.selectedConversation.id,
        this.selectedConversation.phone,
        false
      );
      let step = response?.step;
      if (!step) {
        try {
          const botStatus = await this.api.getAiBotStatus(
            this.selectedConversation.phone
          );
          step = botStatus?.step;
        } catch (error) {
          console.error(error);
        }
      }
      this.selectedConversation.is_assistance_required = false;
      this.selectedConversation.is_bot_enabled = true;
      this.messageText = '';
      if (step) {
        this.selectedConversation.step = step;
      }
      this.conversationResolved.emit(this.selectedConversation);
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoadingConversationAssistant = false;
    }
  }
  // Sin trackBy Angular destruye y recrea todos los app-message-bubble
  // cuando el refresh en background reasigna el array (this.messages), aun
  // si los mensajes son los mismos, produciendo un parpadeo de toda la
  // conversacion. Los optimistas no tienen id/msgId, por eso el fallback a
  // epoch (Date.now(), unico en la practica).
  trackByMessage(_index: number, message: Message): string | number {
    return getMessageKey(message) ?? message.epoch;
  }

  getConversationStep(stepValue: number):
    | {
      name: string;
      value: Steps;
      label: string;
      icon: string;
    }
    | undefined {
    return conversationSteps.find(stepItem => stepItem.value === stepValue);
  }
}
